
import { useActiveArticleStore } from "../store/activeArticleStore";
import { useCollectionStore } from "../store/collectionStore";
import { useUserStore } from "../store/userStore";
import { analyticsClient } from "./client/analyticsClient";
import {
  ANALYTICS_EVENT,
  InteractionPayload,
  DeeplinkPayload,
  BaseAnalyticsPayload,
} from "./types";

const getAdditionalContext = () => {
  const collection = useCollectionStore.getState()?.selectedCollection;
  const activeView = useActiveArticleStore.getState()?.activeView;
  const user = useUserStore.getState()?.user;
  const cxt: any = {};
  if (user) cxt.user_uid = user?.thinkoUser?.uid;
  if (collection?.id) cxt.active_collection = collection?.id;
  if (activeView) cxt.active_view = activeView;
  return cxt;
}
/**
 * Map common fields to PostHog-compatible payload
 * ($screen_name lives ONLY here)
 */
const toPosthogPayload = (
  payload: BaseAnalyticsPayload,
  extra?: Record<string, any>
) => ({
  screen_name: payload.screen_name,
  $screen_name: payload.screen_name,
  ...getAdditionalContext(),
  ...extra,
  ...payload?.context
});


/**
 * Track interaction events (click, swipe)
 */
export const trackInteraction = (payload: InteractionPayload) => {
  if (!analyticsClient) return;
  analyticsClient.capture(
    ANALYTICS_EVENT.INTERACTION,
    toPosthogPayload(payload, {
      action: payload.action,
      component: payload.component,
      direction: payload.direction,
    })
  );
};

/**
 * Track deeplink opened
 */
export const trackDeeplink = (payload: DeeplinkPayload) => {
  if (!analyticsClient) return;

  analyticsClient.capture(
    ANALYTICS_EVENT.DEEPLINK_OPENED,
    toPosthogPayload(payload, {
      deeplink_url: payload.deeplink_url
    })
  );
};

/**
 * Track screen view (web)
 */
export const trackScreenView = (
  screenName: BaseAnalyticsPayload["screen_name"],
  params?: Record<string, any>
) => {
  if (!analyticsClient || !screenName) return;

  analyticsClient.capture(ANALYTICS_EVENT.SCREEN_VIEW, {
    screen_name: screenName,
    $screen_name: screenName,
    ...getAdditionalContext(),
    route_params: params,
  });
};

export const trackScreenTime = (screenName: string, durationMs: number, extras?: Record<string, any>) => {
  analyticsClient?.capture(ANALYTICS_EVENT.SCREEN_TIME, {
    screen_name: screenName,
    $screen_name: screenName,
    duration_ms: durationMs,
    ...getAdditionalContext(),
    ...extras,
  });
};

export const trackAppInstalled = (context?: Record<string, any>) => {
  analyticsClient?.capture(ANALYTICS_EVENT.APP_INSTALLED, {
    ...getAdditionalContext(),
    ...context,
  });
};

function toReportableError(payload: unknown): Error {
  if (payload instanceof Error) return payload;
  if (payload && typeof payload === "object") {
    const p = payload as Record<string, unknown>;
    const msg = String(p.message ?? "Unknown error");
    const err = new Error(msg);
    if (typeof p.stack === "string") err.stack = p.stack;
    return err;
  }
  return new Error(String(payload));
}

/** Extra fields (e.g. source, isFatal) as PostHog properties, excluding message/stack on the Error. */
function exceptionPropertiesFromPayload(payload: unknown): Record<string, unknown> {
  if (!payload || typeof payload !== "object" || payload instanceof Error) return {};
  const { message: _m, stack: _s, ...rest } = payload as Record<string, unknown>;
  return rest;
}

export function flushPostHogBestEffort(): void {
  if (!analyticsClient || process.env.EXPO_PUBLIC_DISABLE_TRACKING === "true") return;
  try {
    const flush = (analyticsClient as { flush?: () => Promise<void> | void }).flush;
    const p = flush?.();
    if (p && typeof (p as Promise<void>).then === "function") {
      void (p as Promise<void>).catch(() => {});
    }
  } catch {
    /* ignore */
  }
}
/**
 * Track app-level errors
 */
export const trackAppError = (error: unknown) => {
    if (!analyticsClient || process.env.EXPO_PUBLIC_DISABLE_TRACKING === "true") return;
  try {
    const err = toReportableError(error);
    const extras = exceptionPropertiesFromPayload(error);
    if (extras.isFatal !== true) return;
    analyticsClient.captureException(err, {
      ...getAdditionalContext(),
      ...extras,
    } as Record<string, any>);
  } catch {
    /* avoid rethrow from analytics */
  }
};

/**
 * Track API errors with normalization
 */
export const trackIdentify = (userId: string, personProperties?: Record<string, any>) => {
  if (!analyticsClient || !userId) return;
  analyticsClient.identify(userId, personProperties);
};

export const trackReset = () => {
  analyticsClient?.reset();
};

export const trackSessionStart = () => {
  analyticsClient?.capture(ANALYTICS_EVENT.SESSION_START, {
    ...getAdditionalContext(),
  });
};

/**
 * Track API errors with normalization
 */
export const trackApiError = (
  error: any,
  source: string = "unknown",
  url?: string
) => {
  const normalizedError =
    error instanceof Error
      ? error
      : new Error(
        typeof error === "string"
          ? error
          : JSON.stringify(error)
      );

  const apiUrl = error?.config?.url ?? url;
  const safeUrl = apiUrl?.split("?")[0];

  console.error(`[${source}] ${apiUrl ?? ""}`, normalizedError);

  analyticsClient?.capture("$capture_error", {
    ...getAdditionalContext(),
    message: `[${safeUrl ?? "no-url"}] ${normalizedError.message}`,
    stack: normalizedError.stack,
    source,
    url: safeUrl,
    method: error?.config?.method,
    status: error?.response?.status,
  });
};
